import { DashboardListItemModel } from 'types/dashboard/DashboardListItemModel';
import { PoorMansError } from 'util/utils';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { postCopyDocument } from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';

import { Card, CardHeading, CardName, CardStatus, CardFormatText, CardDate } from './styles';
import { getCardDateText } from './cardDateText';

type Props = {
  epd: DashboardListItemModel;
  canCopy: boolean;
};

const DynamicEpdCard = ({ epd, canCopy }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isBusy, setIsBusy] = useState(false);

  const handleCopyDocument = async () => {
    const toastId = toast.loading('Copying EPD…', { position: 'top-center' });
    try {
      setIsBusy(true);
      await postCopyDocument(epd.id);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DASHBOARD_SEARCH] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DASHBOARD_QUICK_STATS] });
      toast.update(toastId, { render: 'EPD copied.', type: 'success', isLoading: false, autoClose: 3000 });
    } catch (error) {
      toast.update(toastId, { render: PoorMansError(error), type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setIsBusy(false);
    }
  };

  const confirmCopyDocument = () => {
    confirmAlert({
      caption: 'Are you sure you want to copy this EPD?',
      description: 'A new copy of the EPD will be added to the dashboard.',
      acceptLabel: 'Copy',
      rejectLabel: 'Cancel',
      onConfirm: handleCopyDocument,
    });
  };

  return (
    <Card to={`/dynamic-forms/${epd.id}`}>
      <div>
        <CardStatus status={epd.statusDisplayName}>{t(`epdWizard.epdState.status.${epd.statusDisplayName}`)}</CardStatus>
        <CardHeading>{epd.registrationNumber}</CardHeading>
        <CardName>{epd.name}</CardName>
      </div>
      <div>
        <CardFormatText>Dynamic EPD format</CardFormatText>
        <CardDate>{getCardDateText(epd)}</CardDate>
      </div>
      {canCopy && (
        <CardMenuWrapper>
          <CardMenuButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              confirmCopyDocument();
            }}
            disabled={isBusy}
            title="Copy"
          >
            <i className="pi pi-copy" />
          </CardMenuButton>
        </CardMenuWrapper>
      )}
    </Card>
  );
};

const CardMenuWrapper = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
`;

const CardMenuButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: none;
  cursor: pointer;
  padding: 3px 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.primaryBlack50};
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .pi {
    font-size: 14px;
  }
`;

export default DynamicEpdCard;
